import { useEffect, useState } from "react";
import * as Yup from "yup";
import { XAxis, CartesianGrid, Bar, BarChart, YAxis } from "recharts";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDownIcon, TextSearch } from "lucide-react";
import { format } from "date-fns";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  PageEyebrowLink,
  PageHeader,
  Button,
  MonthYearPicker,
  Label,
  Skeleton,
} from "@/components/ui";
import { useTranslation } from "react-i18next";
import {
  useFetchFinanceCashFlowByRangeDateMutation,
  useFetchFinanceCashFlowReportQuery,
  useToast,
} from "@/hooks";

import { FinanceCashFlowDataTable, MessageError } from "@/components/common";

import { FinanceCashFlows } from "@/types";
import { formatValue, getFormattedDate } from "@/utils";

interface HandleFilterFinanceCashFlowProps {
  date: string;
}

const colors = [
  "#1E7D32", //received (recebido)
  "#95e299", //to_receive (a receber)
  "#D32F2F", //to_pay (a pagar)
  "#FF9800", //paid (pago)
  "#42A5F5", //partial_balance (saldo parcial)
  "#ffb168", //outstanding_balance (saldo pendente)
  "#a1edf7", //expected_balance (saldo previsto)
];

export function FinanceCashFlow() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const [searchParams] = useSearchParams();

  const page = searchParams.get("page");
  const month = searchParams.get("month");
  const perPageParam = searchParams.get("per_page");
  const [per_page, setPer_page] = useState<number>(
    perPageParam ? Number(perPageParam) : 10
  );
  const [date, setDate] = useState({
    startDate: "",
    endDate: "",
  });
  const [financeCashFlow, setFinanceCashFlow] = useState<FinanceCashFlows>();

  const { data, isLoading, refetch } = useFetchFinanceCashFlowReportQuery({
    page,
    month,
    per_page,
  });
  const { mutateAsync, isLoading: isLoadingFinanceCashFlow } =
    useFetchFinanceCashFlowByRangeDateMutation();

  const chartConfig: Record<string, { label: string; color: string }> = {
    received: { label: t("LABEL.received"), color: colors[0] },
    to_receive: { label: t("LABEL.to_receive"), color: colors[1] },
    to_pay: { label: t("LABEL.to_pay"), color: colors[2] },
    paid: { label: t("LABEL.paid"), color: colors[3] },
    partial_balance: { label: t("LABEL.partial_balance"), color: colors[4] },
    outstanding_balance: {
      label: t("LABEL.outstanding_balance"),
      color: colors[5],
    },
    expected_balance: { label: t("LABEL.expected_balance"), color: colors[6] },
  };

  const barConfigs = Object.keys(chartConfig).map((key) => ({
    dataKey: key,
    color: chartConfig[key].color,
  }));

  const formatDayCartData = (value: string) => {
    return getFormattedDate(value, "dayMonth");
  };

  const formatDayToTooltip = (value: string) => {
    return value;
  };

  async function requestFinanceCashFlowByRangeDate(
    startDate: string,
    endDate: string
  ) {

    try {
      await mutateAsync(
        {
          startDate,
          endDate,
          page,
          per_page: 10,
        },
        {
          onSuccess: (data) => {
            setFinanceCashFlow(data);
          },
          onError: () => {
            toast({
              title: t("LABEL.error"),
              description: t("MESSAGE.error_message"),
            });
          },
        }
      );

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t("MESSAGE.error"),
        variant: "destructive",
      });
    }
  }

  const initialValues: HandleFilterFinanceCashFlowProps = {
    date: new Date().toUTCString(),
  };
  const { handleSubmit, errors, touched, ...formik } =
    useFormik<HandleFilterFinanceCashFlowProps>({
      initialValues,
      validationSchema: Yup.object().shape({
        date: Yup.string().required(t("LABEL.required")),
      }),

      onSubmit: async (values) => {
        queryParams.set("month", values.date);
        await navigate({ search: queryParams.toString() });
        // await refetch()
      },
    });
  function handleClear() {
    queryParams.delete("month");
    navigate({ search: queryParams.toString() });
    formik.resetForm();
  }
  useEffect(() => {
    refetch();
  }, [month, refetch]);

  useEffect(() => {
    if (date.startDate && date.endDate) {
      requestFinanceCashFlowByRangeDate(date.startDate, date.endDate);
    }
  }, [page, per_page]);
  return (
    <section className="space-y-6">
      <PageHeader
        eyebrow={
          <PageEyebrowLink to="/financial/account">
            {t("LABEL.financial")}
          </PageEyebrowLink>
        }
        title="Fluxo de caixa"
        lede="Entradas e saídas da clínica no mês, dia a dia."
        actions={
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex items-end gap-2">
              <div className="space-y-2">
                <Label>Filtrar por data</Label>
                <MonthYearPicker

                  date={
                    formik.values.date
                      ? new Date(new Date)
                      : undefined
                  }
                  onChange={(selectedDate) => {
                    if (selectedDate instanceof Date) {
                      formik.setFieldValue(
                        "date",
                        format(selectedDate, "yyyy-MM")
                      );
                    }
                  }}
                  onClear={handleClear}
                  isInvalid={touched.date && errors.date}
                  isValid={touched.date && !errors.date}
                />
              </div>
              <Button className="flex items-center gap-2">
                <TextSearch className="size-4" />
                <span>Consultar</span>
              </Button>
            </div>
            {errors.date && <MessageError messageError={errors.date} />}
          </form>
        }
      />

      {!isLoading && data ? (
        <>
          <div className="w-full mt-10">
            <div className="h-full">
              <ChartContainer
                className="min-h-[100px] max-h-[400px] w-full"
                config={chartConfig}
              >
                <BarChart accessibilityLayer height={200} data={data.chartData}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={formatDayCartData}
                  />
                  <YAxis />
                  <ChartTooltip
                    labelFormatter={formatDayToTooltip}
                    content={<ChartTooltipContent />}
                    wrapperClassName="mt-10"
                  />

                  <ChartLegend content={<ChartLegendContent />} />
                  {barConfigs.map((config) => (
                    <>
                      <Bar
                        key={config.dataKey}
                        barSize={50}
                        dataKey={config.dataKey}
                        fill={config.color}
                      />
                    </>
                  ))}
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </>
      ) : (
        <Skeleton className="w-full max-h-[400px] h-full" />
      )}

      <section className="mt-5 space-y-6">
        <h2 className="text-xl text-zinc-700 font-semibold">Detalhes</h2>

        {isLoading && data ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            <Accordion.Root className="m-0 p-0" type="single" collapsible>
              {data &&
                Array.isArray(data.resultList) &&
                data.resultList.map(({ balance, entries, date, exits }, i) => (
                  <Accordion.Item
                    value={`item-${i}`}
                    key={i}
                    className="bg-zinc-100 border border-zinc-200 rounded w-full flex flex-col mb-3"
                    disabled={isLoadingFinanceCashFlow}
                  >
                    <Accordion.Trigger
                      onClick={() => {
                        setDate({
                          startDate: date,
                          endDate: date,
                        });
                        requestFinanceCashFlowByRangeDate(date, date);
                      }}
                      className="text-xs sm:text-xl flex justify-between items-center px-4 py-2.5 cursor-pointer hover:bg-zinc-200 transition-colors duration-200 gap-2 sm:gap-0"
                    >
                      <div className="flex items-center gap-2 text-base text-zinc-600">
                        <span className="font-bold">
                          {getFormattedDate(date, "dayMonth")}
                        </span>{" "}
                        <span>{getFormattedDate(date, "weekday")}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <div className="flex max-lg:flex-wrap items-center gap-1 sm:gap-2">
                          <div className="bg-green-100 text-green-600 border border-green-300 rounded-full py-1 px-2.5 text-xs">
                            <span className="font-bold">Entrada:</span>{" "}
                            <span>{formatValue(entries)}</span>
                          </div>
                          <div className="bg-red-100 text-red-600 border border-red-300 rounded-full py-1 px-2.5 text-xs">
                            <span className="font-bold">Saída:</span>{" "}
                            <span>{formatValue(exits)}</span>
                          </div>
                          <div className="bg-yellow-100 text-yellow-600 border border-yellow-300 rounded-full py-1 px-2.5 text-xs">
                            <span className="font-bold">Saldo:</span>{" "}
                            {formatValue(balance)}
                          </div>
                        </div>
                        <ChevronDownIcon
                          className="transition-transform duration-200 transform text-zinc-400 data-[state=open]:rotate-180 size-5"
                          aria-hidden
                        />
                      </div>
                    </Accordion.Trigger>
                    <Accordion.Content className="p-4 bg-white">
                      {!financeCashFlow || isLoadingFinanceCashFlow ? (
                        <Skeleton className="w-full h-96" />
                      ) : (
                        <FinanceCashFlowDataTable
                          handlePerPageChange={setPer_page}
                          per_page={per_page}
                          response={financeCashFlow}
                        />
                      )}
                    </Accordion.Content>
                  </Accordion.Item>
                ))}
            </Accordion.Root>
          </>
        )}
      </section>
    </section>
  );
}
